import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserMenu from '../components/UserMenu';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import axios from 'axios';
import {
  actualizarAnalisis,
  crearAnalisis,
  enviarEmail,
  EstudioOption,
  getAnalisis,
  getEmailSugerido,
  getEstudios,
  getLaboratorios,
  LaboratorioOption,
  verPdf,
} from '../api/analisisApi';
import { buscarPacientes, PacienteResponse } from '../api/pacienteApi';
import { ApiError } from '../types/auth.types';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import AlertCard from '../components/AlertCard';
import styles from './NuevoAnalisisPage.module.css';

interface SelectedItem {
  resultado: string;
  laboratorioId: number;
}

const HEMOGRAMA_ESTUDIO_ID = 176;
const ORINA_COMPLETA_ESTUDIO_ID = 240;

// Estudios cuyo resultado se compone de varios campos individuales (separados por "|" en
// vez de un único valor de texto). Las clases CSS de este bloque siguen llamándose
// "hemograma*" por motivos históricos, pero el layout es genérico para cualquier estudio listado acá.
// Lookup por ID numérico (hemograma y orina completa, ya establecidos).
const CAMPOS_MULTIPLES: Record<number, string[]> = {
  [HEMOGRAMA_ESTUDIO_ID]: [
    'Eritrocitos', 'Hemoglobina', 'Hematocrito', 'V.C.M', 'H.C.M', 'M.C.H.C', 'RDW',
    'Leucocitos', 'Metamielocitos', 'Neutrófilos Cayados', 'Neutrófilos Segmentados',
    'Eosinófilos', 'Basófilos', 'Linfocitos', 'Monocitos',
  ],
  [ORINA_COMPLETA_ESTUDIO_ID]: [
    'Color', 'Aspecto', 'Sedimento', 'Densidad', 'Reacción', 'PH', 'Proteínas', 'Glucosa',
    'Cetonas', 'Pigmentos Biliares', 'Urobilinogeno', 'Hemoglobina', 'Nitritos',
    'Células epiteliales planas', 'Leucocitos', 'Hematíes', 'Filamentos de mucus',
  ],
};

// Lookup por código de estudio (string), para estudios cuyo código puede incluir decimales.
const CAMPOS_MULTIPLES_CODIGO: Record<string, string[]> = {
  // Proteinogramas (8 campos: {valor1}..{valor7} + relación {valor8})
  '764':     ['Proteínas Totales', 'Albúmina', 'Alfa-1 Globulinas', 'Alfa-2 Globulinas', 'Beta 1 Globulinas', 'Beta 2 Globulinas', 'Gamma-Globulinas', 'Relación Alb/Glob'],
  '765':     ['Proteínas Totales', 'Albúmina', 'Alfa-1 Globulinas', 'Alfa-2 Globulinas', 'Beta 1 Globulinas', 'Beta 2 Globulinas', 'Gamma-Globulinas', 'Relación Alb/Glob'],
  '766':     ['Proteínas Totales', 'Albúmina', 'Alfa-1 Globulinas', 'Alfa-2 Globulinas', 'Beta 1 Globulinas', 'Beta 2 Globulinas', 'Gamma-Globulinas', 'Relación Alb/Glob'],
  '923':     ['Proteínas Totales', 'Albúmina', 'Alfa-1 Globulinas', 'Alfa-2 Globulinas', 'Beta 1 Globulinas', 'Beta 2 Globulinas', 'Gamma-Globulinas', 'Relación Alb/Glob'],
  '416':     ['Proteínas Totales', 'Albúmina', 'Alfa-1 Globulinas', 'Alfa-2 Globulinas', 'Beta 1 Globulinas', 'Beta 2 Globulinas', 'Gamma-Globulinas', 'Relación Alb/Glob'],
  // Hemoglobinas
  '471':     ['Hb A (A0 + A1)', 'Hb A2', 'Hb F', 'Otras Hemoglobinas'],
  '5802':    ['Hb A (A0 + A1)', 'Hb A2'],
  '846':     ['Hb A2'],
  // Ionogramas
  '546':     ['Sodio', 'Potasio', 'Cloro'],
  '547':     ['Sodio', 'Potasio'],
  // Microalbuminuria
  '1130.24': ['Microalbuminuria (ug/ml)', 'Microalbuminuria 24 hs (ug/min)', 'Diuresis (ml/24hs)'],
  '1130.00': ['Microalbuminuria al azar', 'Creatinina urinaria al azar', 'Ratio ug/mg creatinina'],
  // Hierro
  '875':     ['Transferrina', 'Capacidad total de fijación', 'Capacidad latente de fijación', 'Capacidad de Saturación'],
  // Calcio
  '134':     ['Calcio iónico (mg/dl)', 'Porcentaje de Ionización (%)'],
  // Linfocitos
  '1011':    ['Glóbulos Blancos', 'Linfocitos (%)', 'CD4 (%)', 'CD4 Totales'],
  '1015':    ['Glóbulos Blancos', 'Linfocitos (%)', 'CD8 (%)', 'CD8 Totales'],
  // Grupo y factor
  '433':     ['Grupo Sanguíneo', 'Factor Rh'],
  // Coprocultivo / parasitología
  '736':     ['Observación Macroscópica', 'Observación Microscópica', 'Ensayo de Enriquecimiento', 'Test de Graham'],
  // ANCA
  '3734':    ['cANCA Ac(citoplasmático)', 'pANCA Ac(perinuclear)'],
  // Tiempos de coagulación
  '169':     ['Tiempo de Coagulación', 'Tiempo de Sangría'],
  // Bilirrubina
  '110':     ['Total', 'Directa', 'Indirecta'],
  // Proteinuria al azar
  '6714':    ['Proteinuria al azar', 'Creatinina urinaria al azar', 'Relación Proteína/Creatinina urinaria al azar'],
  // Carga viral
  '1105':    ['Resultado', 'Copias', 'Log'],
};

// Estudios cuyo resultado se escribe libremente y se vuelca tal cual al PDF (sin plantilla).
const ESTUDIOS_CONTENIDO_DIRECTO = new Set(['665', '187', '911', '2709', '56']);

// Índices de campos de Orina Completa que deben registrarse como "No contiene" si el usuario los deja vacíos.
const ORINA_NO_CONTIENE_INDICES = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

// Índices del array de campos del hemograma que componen la fórmula leucocitaria: deben sumar 100.
const FORMULA_LEUCOCITARIA_INDICES = [8, 9, 10, 11, 12, 13, 14];

function esEstudioMultiple(estudioId: number, codigo?: string): boolean {
  return estudioId in CAMPOS_MULTIPLES || (codigo !== undefined && codigo in CAMPOS_MULTIPLES_CODIGO);
}

function getCampos(estudioId: number, codigo?: string): string[] {
  return CAMPOS_MULTIPLES[estudioId] ?? (codigo !== undefined ? (CAMPOS_MULTIPLES_CODIGO[codigo] ?? []) : []);
}

function getSubValores(estudioId: number, codigo: string | undefined, resultado: string): string[] {
  const campos = getCampos(estudioId, codigo);
  const partes = resultado.split('|');
  return campos.map((_, i) => partes[i] ?? '');
}

function setSubValor(estudioId: number, codigo: string | undefined, resultado: string, index: number, valor: string): string {
  const valores = getSubValores(estudioId, codigo, resultado);
  valores[index] = valor;
  return valores.join('|');
}

function sumaFormulaLeucocitaria(resultado: string): number {
  const valores = getSubValores(HEMOGRAMA_ESTUDIO_ID, undefined, resultado);
  return FORMULA_LEUCOCITARIA_INDICES.reduce((acc, i) => acc + (parseFloat(valores[i]) || 0), 0);
}

function today(): string {
  return new Date().toISOString().split('T')[0];
}

function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

export default function NuevoAnalisisPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id?: string }>();
  const editMode = !!editId;

  const [laboratorios, setLaboratorios] = useState<LaboratorioOption[]>([]);
  const [estudios, setEstudios] = useState<EstudioOption[]>([]);

  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState<PacienteResponse[]>([]);
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PacienteResponse | null>(null);
  const [patientSearched, setPatientSearched] = useState(false);

  const [fecha, setFecha] = useState(today());
  const [medicoSolicitante, setMedicoSolicitante] = useState('');
  const [otroFilter, setOtroFilter] = useState('');
  const [selected, setSelected] = useState<Map<number, SelectedItem>>(new Map());
  const [observacion, setObservacion] = useState('');

  const [reviewing, setReviewing] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formulaError, setFormulaError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdAnalisisId, setCreatedAnalisisId] = useState<string | null>(null);

  const [studyObsEnabled, setStudyObsEnabled] = useState<Set<number>>(new Set());
  const [studyObs, setStudyObs] = useState<Map<number, string>>(new Map());
  const [studyDiuresisEnabled, setStudyDiuresisEnabled] = useState<Set<number>>(new Set());
  const [studyDiuresis, setStudyDiuresis] = useState<Map<number, string>>(new Map());

  const [viewingPdf, setViewingPdf] = useState(false);
  const [emailLookupLoading, setEmailLookupLoading] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailFound, setEmailFound] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getLaboratorios(), getEstudios()])
      .then(([labs, ests]) => {
        setLaboratorios(labs);
        setEstudios(ests);
      })
      .finally(() => setLoadingData(false));
  }, []);

  useEffect(() => {
    if (!editMode || loadingData || !editId) return;
    getAnalisis(editId).then((data) => {
      setFecha(data.fecha ?? today());
      setMedicoSolicitante(data.medicoSolicitante ?? '');
      setObservacion(data.observacion ?? '');
      setSelectedPatient({
        id: '',
        nombre: data.pacienteNombre,
        apellido: data.pacienteApellido,
        dni: data.pacienteDni,
        email: null,
        particular: false,
        obraSocial: null,
        nroAfiliado: null,
        createdAt: '',
      });
      const map = new Map<number, SelectedItem>();
      const obsEnabled = new Set<number>();
      const obsMap = new Map<number, string>();
      const diuresisEnabled = new Set<number>();
      const diuresisMap = new Map<number, string>();
      (data.items ?? []).forEach((item) => {
        let resultado = item.resultado ?? '';
        // OBS siempre se codifica al final → se extrae primero
        const obsIdx = resultado.indexOf('\nOBS:');
        if (obsIdx !== -1) {
          obsMap.set(item.estudioId, resultado.slice(obsIdx + 5));
          obsEnabled.add(item.estudioId);
          resultado = resultado.slice(0, obsIdx);
        }
        // DIURESIS va antes de OBS → se extrae después de quitar OBS
        const diuresisIdx = resultado.indexOf('\nDIURESIS:');
        if (diuresisIdx !== -1) {
          diuresisMap.set(item.estudioId, resultado.slice(diuresisIdx + 10));
          diuresisEnabled.add(item.estudioId);
          resultado = resultado.slice(0, diuresisIdx);
        }
        map.set(item.estudioId, { resultado, laboratorioId: item.laboratorioId });
      });
      setSelected(map);
      setStudyObsEnabled(obsEnabled);
      setStudyObs(obsMap);
      setStudyDiuresisEnabled(diuresisEnabled);
      setStudyDiuresis(diuresisMap);
    }).catch(() => setError('No se pudieron cargar los datos del análisis.'));
  }, [editMode, loadingData, editId]);

  const defaultLabId = laboratorios.find((l) => l.id === 1)?.id ?? laboratorios[0]?.id ?? 0;

  // Un mismo código de estudio puede existir para varios laboratorios (mismo código, distinto
  // id_laboratorio). En el buscador solo se muestra la versión del laboratorio 1 (o, si no existe,
  // la primera disponible) para no listar el mismo estudio repetido; el laboratorio real se elige
  // después con el desplegable de Laboratorio en cada resultado seleccionado.
  const dedupedEstudios = React.useMemo(() => {
    const porCodigo = new Map<string, EstudioOption>();
    for (const e of estudios) {
      const actual = porCodigo.get(e.codigo);
      if (!actual || (e.laboratorioId === 1 && actual.laboratorioId !== 1)) {
        porCodigo.set(e.codigo, e);
      }
    }
    return Array.from(porCodigo.values());
  }, [estudios]);

  async function handlePatientSearch(e: FormEvent) {
    e.preventDefault();
    if (!patientQuery.trim()) return;
    setSearchingPatient(true);
    setPatientSearched(true);
    try {
      const results = await buscarPacientes(patientQuery.trim());
      setPatientResults(results);
    } finally {
      setSearchingPatient(false);
    }
  }

  function selectPatient(p: PacienteResponse) {
    setSelectedPatient(p);
    setPatientResults([]);
    setPatientQuery('');
    setPatientSearched(false);
  }

  function clearPatient() {
    setSelectedPatient(null);
    setPatientResults([]);
    setPatientQuery('');
    setPatientSearched(false);
  }

  function toggleEstudio(estudio: EstudioOption) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(estudio.id)) {
        next.delete(estudio.id);
      } else {
        next.set(estudio.id, { resultado: '', laboratorioId: defaultLabId });
      }
      return next;
    });
    setStudyObsEnabled((prev) => { const next = new Set(prev); next.delete(estudio.id); return next; });
    setStudyObs((prev) => { const next = new Map(prev); next.delete(estudio.id); return next; });
    setStudyDiuresisEnabled((prev) => { const next = new Set(prev); next.delete(estudio.id); return next; });
    setStudyDiuresis((prev) => { const next = new Map(prev); next.delete(estudio.id); return next; });
  }

  function updateItem(estudioId: number, patch: Partial<SelectedItem>) {
    setSelected((prev) => {
      const next = new Map(prev);
      const cur = next.get(estudioId);
      if (cur) next.set(estudioId, { ...cur, ...patch });
      return next;
    });
  }

  function resetForm() {
    setSuccess(false);
    setCreatedAnalisisId(null);
    setEmailValue('');
    setEmailFound(false);
    setEmailSentTo(null);
    setEmailError(null);
    setReviewing(false);
    setSelectedPatient(null);
    setSelected(new Map());
    setStudyObsEnabled(new Set());
    setStudyObs(new Map());
    setStudyDiuresisEnabled(new Set());
    setStudyDiuresis(new Map());
    setObservacion('');
    setFecha(today());
    setMedicoSolicitante('');
    setOtroFilter('');
    setPatientSearched(false);
    setError(null);
  }

  useEffect(() => {
    if (!createdAnalisisId) return;
    setEmailLookupLoading(true);
    getEmailSugerido(createdAnalisisId)
      .then((res) => {
        setEmailFound(res.encontrado);
        setEmailValue(res.email ?? '');
      })
      .finally(() => setEmailLookupLoading(false));
  }, [createdAnalisisId]);

  async function handleVerPdf() {
    if (!createdAnalisisId) return;
    setViewingPdf(true);
    try {
      await verPdf(createdAnalisisId);
    } finally {
      setViewingPdf(false);
    }
  }

  async function handleEnviarEmail() {
    if (!createdAnalisisId || !emailValue.trim()) return;
    setSendingEmail(true);
    setEmailError(null);
    setEmailSentTo(null);
    try {
      await enviarEmail(createdAnalisisId, emailValue.trim());
      setEmailSentTo(emailValue.trim());
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setEmailError(err.response?.data?.mensaje ?? 'Error al enviar el email.');
      } else {
        setEmailError('Error al enviar el email.');
      }
    } finally {
      setSendingEmail(false);
    }
  }

  function handleReview(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFormulaError(null);

    if (!selectedPatient) { setError('Seleccioná un paciente antes de continuar.'); return; }
    if (selected.size === 0) { setError('Seleccioná al menos un estudio.'); return; }

    const hemogramaItem = selected.get(HEMOGRAMA_ESTUDIO_ID);
    if (hemogramaItem) {
      const suma = sumaFormulaLeucocitaria(hemogramaItem.resultado);
      if (Math.abs(suma - 100) > 0.01) {
        setFormulaError(
          `La suma de Metamielocitos, Neutrófilos Cayados, Neutrófilos Segmentados, Eosinófilos, ` +
          `Basófilos, Linfocitos y Monocitos debe ser igual a 100 (actualmente suma ${suma}).`
        );
        return;
      }
    }

    setReviewing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleConfirm() {
    setError(null);
    setLoading(true);
    const itemsPayload = Array.from(selected.entries()).map(([estudioId, item]) => {
      let resultado = item.resultado;

      if (estudioId === ORINA_COMPLETA_ESTUDIO_ID) {
        const campos = getCampos(estudioId, undefined);
        const partes = resultado.split('|');
        const vals = campos.map((_, i) => partes[i] ?? '');
        ORINA_NO_CONTIENE_INDICES.forEach((i) => {
          if (!vals[i] || vals[i].trim() === '') vals[i] = 'No contiene';
        });
        resultado = vals.join('|');
      }

      const diuresis = studyDiuresis.get(estudioId);
      if (studyDiuresisEnabled.has(estudioId) && diuresis && diuresis.trim()) {
        resultado = resultado + '\nDIURESIS:' + diuresis.trim();
      }

      const obs = studyObs.get(estudioId);
      if (studyObsEnabled.has(estudioId) && obs && obs.trim()) {
        resultado = resultado + '\nOBS:' + obs.trim();
      }
      return { estudioId, resultado, laboratorioId: item.laboratorioId };
    });
    try {
      if (editMode && editId) {
        await actualizarAnalisis(editId, {
          fecha,
          observacion: observacion.trim(),
          medicoSolicitante: medicoSolicitante.trim(),
          items: itemsPayload,
        });
        setCreatedAnalisisId(editId);
      } else {
        const creado = await crearAnalisis({
          pacienteId: selectedPatient!.id,
          fecha,
          observacion: observacion.trim(),
          medicoSolicitante: medicoSolicitante.trim(),
          items: itemsPayload,
        });
        setCreatedAnalisisId(creado.id);
      }
      setSuccess(true);
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'Error al guardar el análisis.');
      } else {
        setError('Error al guardar el análisis.');
      }
      setReviewing(false);
    } finally {
      setLoading(false);
    }
  }

  const frecuentes = dedupedEstudios.filter((e) => e.frecuente);

  const otrosResults = otroFilter.trim()
    ? dedupedEstudios.filter((e) => {
        if (e.frecuente) return false;
        const q = otroFilter.toLowerCase();
        return e.descripcion.toLowerCase().includes(q) || e.codigo.toLowerCase().includes(q);
      })
    : [];

  const selectedEstudios = Array.from(selected.keys())
    .map((id) => estudios.find((e) => e.id === id))
    .filter((e): e is EstudioOption => e !== undefined);

  // ── Pantalla de éxito ──
  if (success) {
    return (
      <div className={styles.page}>
        <header className={styles.topbar}>
          <button className={styles.backBtn} onClick={() => navigate(editMode ? '/buscar-analisis' : '/dashboard')}>← Volver</button>
          <img src={logo} alt="Logo" className={styles.topbarLogo} />
          <p className={styles.topbarTitle}>{editMode ? 'Editar análisis' : 'Nuevo análisis'}</p>
          <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
          <UserMenu />
        </header>
        <main className={styles.content}>
          <div className={styles.successCard}>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.successMsg}>Análisis guardado correctamente</p>

            <button
              type="button"
              className={styles.viewPdfBtn}
              onClick={handleVerPdf}
              disabled={viewingPdf || !createdAnalisisId}
            >
              {viewingPdf ? 'Abriendo PDF...' : 'Ver PDF'}
            </button>

            <div className={styles.emailSection}>
              <p className={styles.emailSectionTitle}>
                Si todo está correcto, envialo por email al paciente
              </p>

              {emailLookupLoading ? (
                <p className={styles.emailHint}>Buscando el email del paciente...</p>
              ) : (
                <>
                  {!emailFound && (
                    <p className={styles.emailHint}>
                      No encontramos una cuenta asociada al DNI del paciente. Ingresá el email manualmente.
                    </p>
                  )}
                  <div className={styles.searchRow}>
                    <input
                      className={styles.searchInput}
                      type="email"
                      value={emailValue}
                      onChange={(e) => { setEmailValue(e.target.value); setEmailSentTo(null); setEmailError(null); }}
                      placeholder="email@ejemplo.com"
                    />
                    <button
                      type="button"
                      className={styles.searchBtn}
                      onClick={handleEnviarEmail}
                      disabled={sendingEmail || !emailValue.trim()}
                    >
                      {sendingEmail ? 'Enviando...' : 'Enviar por email'}
                    </button>
                  </div>
                  {emailSentTo && (
                    <p className={styles.emailSuccess}>✓ Se envió el resultado a {emailSentTo}.</p>
                  )}
                  {emailError && <p className={styles.emailErrorText}>{emailError}</p>}
                </>
              )}
            </div>

            <div className={styles.successActions}>
              <Button type="button" onClick={resetForm}>Cargar otro análisis</Button>
              <button type="button" className={styles.linkBtn} onClick={() => navigate('/dashboard')}>Ir al inicio</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Pantalla de revisión ──
  if (reviewing) {
    const labName = (id: number) => laboratorios.find((l) => l.id === id)?.nombre ?? '-';

    return (
      <div className={styles.page}>
        <header className={styles.topbar}>
          <button className={styles.backBtn} onClick={() => setReviewing(false)}>← Editar</button>
          <img src={logo} alt="Logo" className={styles.topbarLogo} />
          <p className={styles.topbarTitle}>{editMode ? 'Confirmar edición' : 'Confirmar análisis'}</p>
          <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
          <UserMenu />
        </header>
        <main className={styles.content}>
          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

          <div className={styles.reviewIntro}>
            <svg
              className={styles.reviewIntroIcon}
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p className={styles.reviewIntroText}>
              Revisá los datos antes de confirmar. Una vez guardado no se podrá modificar.
            </p>
          </div>

          {/* Paciente */}
          <div className={styles.card}>
            <h2 className={styles.reviewSectionTitle}>Paciente</h2>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>Nombre</span>
              <span className={styles.reviewValue}>
                {selectedPatient!.apellido}, {selectedPatient!.nombre}
              </span>
            </div>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>DNI</span>
              <span className={styles.reviewValue}>{selectedPatient!.dni}</span>
            </div>
            {selectedPatient!.obraSocial && (
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Obra social</span>
                <span className={styles.reviewValue}>{selectedPatient!.obraSocial}</span>
              </div>
            )}
          </div>

          {/* Médico solicitante */}
          {medicoSolicitante.trim() && (
            <div className={styles.card}>
              <h2 className={styles.reviewSectionTitle}>Médico solicitante</h2>
              <div className={styles.reviewRow}>
                <span className={styles.reviewLabel}>Nombre</span>
                <span className={styles.reviewValue}>{medicoSolicitante}</span>
              </div>
            </div>
          )}

          {/* Fecha */}
          <div className={styles.card}>
            <h2 className={styles.reviewSectionTitle}>Fecha del análisis</h2>
            <div className={styles.reviewRow}>
              <span className={styles.reviewLabel}>Fecha de realización</span>
              <span className={styles.reviewValue}>{formatDate(fecha)}</span>
            </div>
          </div>

          {/* Estudios y resultados */}
          <div className={styles.card}>
            <h2 className={styles.reviewSectionTitle}>
              Estudios
              <span className={styles.reviewBadge}>{selectedEstudios.length}</span>
            </h2>
            {selectedEstudios.some((e) => !esEstudioMultiple(e.id, e.codigo)) && (
              <div className={styles.reviewStudyList}>
                <div className={styles.reviewStudyHeader}>
                  <span>Estudio</span>
                  <span>Resultado</span>
                  <span>Laboratorio</span>
                </div>
                {selectedEstudios.filter((e) => !esEstudioMultiple(e.id, e.codigo)).map((e, idx) => {
                  const item = selected.get(e.id)!;
                  const studyObsText = studyObsEnabled.has(e.id) ? (studyObs.get(e.id) ?? '') : '';
                  return (
                    <div key={e.id} className={`${styles.reviewStudyRow} ${idx % 2 === 0 ? styles.reviewStudyRowEven : ''}`}>
                      <div className={styles.reviewStudyName}>
                        <span className={styles.reviewValue}>{e.descripcion}</span>
                        {e.unidadMedida && <span className={styles.reviewStudyUnit}>{e.unidadMedida}</span>}
                      </div>
                      <span className={styles.reviewValue}>
                        {item.resultado || <em className={styles.reviewEmpty}>Sin resultado</em>}
                      </span>
                      <span className={styles.reviewValue}>{labName(item.laboratorioId)}</span>
                      {studyObsText.trim() && (
                        <p className={styles.reviewStudyObs}>Obs: {studyObsText}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedEstudios.filter((e) => esEstudioMultiple(e.id, e.codigo)).map((e) => {
              const item = selected.get(e.id)!;
              const valores = getSubValores(e.id, e.codigo, item.resultado);
              const studyObsText = studyObsEnabled.has(e.id) ? (studyObs.get(e.id) ?? '') : '';
              const studyDiuresisText = e.id === ORINA_COMPLETA_ESTUDIO_ID && studyDiuresisEnabled.has(e.id) ? (studyDiuresis.get(e.id) ?? '') : '';
              return (
                <div key={e.id} className={styles.reviewHemogramaBlock}>
                  <div className={styles.reviewHemogramaHeader}>
                    <span className={styles.reviewValue}>{e.descripcion}</span>
                    <span className={styles.reviewStudyUnit}>{labName(item.laboratorioId)}</span>
                  </div>
                  <div className={styles.reviewHemogramaGrid}>
                    {getCampos(e.id, e.codigo).map((campo, i) => (
                      valores[i].trim() && (
                        <div key={campo} className={styles.reviewHemogramaItem}>
                          <span className={styles.reviewHemogramaLabel}>{campo}</span>
                          <span className={styles.reviewHemogramaValue}>{valores[i]}</span>
                        </div>
                      )
                    ))}
                  </div>
                  {studyDiuresisText.trim() && (
                    <p className={styles.reviewStudyObs}>Diuresis: {studyDiuresisText}</p>
                  )}
                  {studyObsText.trim() && (
                    <p className={styles.reviewStudyObs}>Obs: {studyObsText}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Observación */}
          {observacion.trim() && (
            <div className={styles.card}>
              <h2 className={styles.reviewSectionTitle}>Observación</h2>
              <p className={styles.reviewObservacion}>{observacion}</p>
            </div>
          )}

          {/* Acciones */}
          <div className={styles.reviewActions}>
            <Button type="button" loading={loading} loadingText="Guardando..." onClick={handleConfirm}>
              Confirmar y guardar
            </Button>
            <button type="button" className={styles.editBtn} onClick={() => setReviewing(false)} disabled={loading}>
              Volver a editar
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Formulario principal ──
  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>← Volver</button>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>{editMode ? 'Editar análisis' : 'Nuevo análisis'}</p>
        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        {loadingData ? (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Cargando datos...</p>
          </div>
        ) : (
        <form onSubmit={handleReview} noValidate>
          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

          <AlertCard
            isVisible={!!formulaError}
            icon="⚠"
            title="Error en la fórmula leucocitaria"
            description={formulaError ?? ''}
            buttonText="Entendido"
            onButtonClick={() => setFormulaError(null)}
            onDismiss={() => setFormulaError(null)}
          />

          <div className={styles.topRow}>
          {/* ── 1. Paciente ── */}
          <div className={`${styles.card} ${styles.cardPatient}`}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardStep}>1</span>
              Paciente
            </h2>

            {selectedPatient ? (
              <div className={styles.selectedPatient}>
                <div className={styles.selectedPatientInfo}>
                  <div className={styles.selectedPatientName}>
                    {selectedPatient.apellido}, {selectedPatient.nombre}
                  </div>
                  <div className={styles.selectedPatientDni}>DNI {selectedPatient.dni}</div>
                </div>
                {!editMode && (
                  <button type="button" className={styles.clearBtn} onClick={clearPatient} title="Cambiar paciente">
                    ✕
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className={styles.searchRow}>
                  <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Buscar por nombre, apellido o DNI..."
                    value={patientQuery}
                    onChange={(e) => setPatientQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handlePatientSearch(e as any); }
                    }}
                  />
                  <button
                    type="button"
                    className={styles.searchBtn}
                    onClick={handlePatientSearch}
                    disabled={searchingPatient || !patientQuery.trim()}
                  >
                    {searchingPatient ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>

                {patientResults.length > 0 ? (
                  <ul className={styles.patientList}>
                    {patientResults.map((p) => (
                      <li key={p.id} className={styles.patientItem} onClick={() => selectPatient(p)}>
                        <div className={styles.patientItemName}>{p.apellido}, {p.nombre}</div>
                        <div className={styles.patientItemDni}>DNI {p.dni}</div>
                      </li>
                    ))}
                  </ul>
                ) : patientSearched && !searchingPatient ? (
                  <p className={styles.noResults}>No se encontró ningún paciente con ese criterio.</p>
                ) : null}

                <div className={styles.createPatientRow}>
                  <span className={styles.createPatientHint}>¿El paciente no está cargado?</span>
                  <a
                    href="/pacientes?crear=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.createPatientLink}
                  >
                    Crear paciente →
                  </a>
                </div>
              </>
            )}
          </div>

          {/* ── 2. Médico solicitante ── */}
          <div className={`${styles.card} ${styles.cardMedico}`}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardStep}>2</span>
              Médico solicitante
            </h2>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="medicoSolicitante">Nombre del médico</label>
              <input
                id="medicoSolicitante"
                type="text"
                className={styles.medicoInput}
                value={medicoSolicitante}
                onChange={(e) => setMedicoSolicitante(e.target.value)}
                placeholder="Dr/Dra..."
              />
            </div>
          </div>

          {/* ── 3. Fecha ── */}
          <div className={`${styles.card} ${styles.cardFecha}`}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardStep}>3</span>
              Fecha del análisis
            </h2>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="fecha">Fecha de realización</label>
              <input
                id="fecha"
                type="date"
                className={styles.dateInput}
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                max={today()}
              />
            </div>
          </div>
          </div>{/* fin topRow */}

          {/* ── 4. Estudios ── */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardStep}>4</span>
              Estudios
            </h2>

            {frecuentes.length > 0 && (
              <div className={styles.studyGroup}>
                <p className={styles.studyGroupTitle}>Frecuentes</p>
                <div className={styles.studyList}>
                  {frecuentes.map((e) => (
                    <label key={e.id} className={`${styles.studyCheckbox} ${selected.has(e.id) ? styles.studyCheckboxChecked : ''}`}>
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggleEstudio(e)}
                      />
                      <span className={styles.studyInfo}>
                        <span className={styles.studyDesc}>{e.descripcion}</span>
                        <span className={styles.studyCodigo}>{e.codigo}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.studyGroup}>
              <p className={styles.studyGroupTitle}>Buscar otro estudio</p>
              <input
                className={styles.studyFilter}
                type="text"
                placeholder="Escribí para buscar estudios no frecuentes..."
                value={otroFilter}
                onChange={(e) => setOtroFilter(e.target.value)}
              />
              {otroFilter.trim() && (
                otrosResults.length > 0 ? (
                  <div className={styles.studyList}>
                    {otrosResults.map((e) => (
                      <label key={e.id} className={`${styles.studyCheckbox} ${selected.has(e.id) ? styles.studyCheckboxChecked : ''}`}>
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggleEstudio(e)}
                        />
                        <span className={styles.studyInfo}>
                          <span className={styles.studyDesc}>{e.descripcion}</span>
                          <span className={styles.studyCodigo}>{e.codigo}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className={styles.emptyStudies}>No se encontraron estudios para "{otroFilter}".</p>
                )
              )}
            </div>

            {selected.size > 0 && (
              <div className={styles.selectedCount}>
                {selected.size} estudio{selected.size !== 1 ? 's' : ''} seleccionado{selected.size !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* ── 5. Resultados ── */}
          {selectedEstudios.length > 0 && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>
                <span className={styles.cardStep}>5</span>
                Resultados
              </h2>
              {selectedEstudios.some((e) => !esEstudioMultiple(e.id, e.codigo)) && (
              <div className={styles.resultList}>
                {selectedEstudios.filter((e) => !esEstudioMultiple(e.id, e.codigo)).map((e, idx) => {
                  const item = selected.get(e.id)!;
                  return (
                    <div key={e.id} className={`${styles.resultRow} ${idx % 2 === 0 ? styles.resultRowEven : ''} ${ESTUDIOS_CONTENIDO_DIRECTO.has(e.codigo) ? styles.resultRowTop : ''}`}>
                      <div className={styles.resultEstudioCol}>
                        <div className={styles.resultEstudio}>{e.descripcion}</div>
                        {e.unidadMedida && <div className={styles.resultUnidad}>{e.unidadMedida}</div>}
                      </div>
                      <div className={styles.resultInputCol}>
                        <label className={styles.label}>
                          {ESTUDIOS_CONTENIDO_DIRECTO.has(e.codigo) ? 'Contenido del PDF' : 'Resultado'}
                        </label>
                        {ESTUDIOS_CONTENIDO_DIRECTO.has(e.codigo) ? (
                          <textarea
                            className={`${styles.resultInput} ${styles.resultInputDirecto}`}
                            value={item.resultado}
                            onChange={(ev) => updateItem(e.id, { resultado: ev.target.value })}
                            placeholder="Escribí el contenido que aparecerá en el PDF..."
                            rows={4}
                          />
                        ) : (
                          <input
                            className={styles.resultInput}
                            type="text"
                            value={item.resultado}
                            onChange={(ev) => updateItem(e.id, { resultado: ev.target.value })}
                            placeholder="Ingresá el resultado..."
                          />
                        )}
                      </div>
                      <div className={styles.resultLabCol}>
                        <label className={styles.label}>Laboratorio</label>
                        <select
                          className={styles.labSelect}
                          value={item.laboratorioId}
                          onChange={(ev) => updateItem(e.id, { laboratorioId: Number(ev.target.value) })}
                        >
                          {laboratorios.map((l) => (
                            <option key={l.id} value={l.id}>{l.nombre}</option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.resultRemoveCol}>
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => toggleEstudio(e)}
                          title="Quitar estudio"
                        >
                          ✕ Quitar
                        </button>
                      </div>
                      <div className={styles.studyObsWrapper}>
                        <div className={styles.studyObsCheckRow}>
                          <label className={styles.studyObsCheckLabel}>
                            <input
                              type="checkbox"
                              checked={studyObsEnabled.has(e.id)}
                              onChange={(ev) => setStudyObsEnabled((prev) => {
                                const next = new Set(prev);
                                if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                                return next;
                              })}
                            />
                            Agregar observación
                          </label>
                          <label className={styles.studyObsCheckLabel}>
                            <input
                              type="checkbox"
                              checked={studyDiuresisEnabled.has(e.id)}
                              onChange={(ev) => setStudyDiuresisEnabled((prev) => {
                                const next = new Set(prev);
                                if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                                return next;
                              })}
                            />
                            Diuresis
                          </label>
                        </div>
                        {studyObsEnabled.has(e.id) && (
                          <textarea
                            className={styles.studyObsTextarea}
                            value={studyObs.get(e.id) ?? ''}
                            onChange={(ev) => setStudyObs((prev) => { const next = new Map(prev); next.set(e.id, ev.target.value); return next; })}
                            placeholder="Observación del estudio..."
                            rows={2}
                          />
                        )}
                        {studyDiuresisEnabled.has(e.id) && (
                          <input
                            className={styles.resultInput}
                            type="text"
                            value={studyDiuresis.get(e.id) ?? ''}
                            onChange={(ev) => setStudyDiuresis((prev) => { const next = new Map(prev); next.set(e.id, ev.target.value); return next; })}
                            placeholder="ej: 1.350 ml/24 hs"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {selectedEstudios.filter((e) => esEstudioMultiple(e.id, e.codigo)).map((e) => {
                const item = selected.get(e.id)!;
                const valores = getSubValores(e.id, e.codigo, item.resultado);
                const labInline = e.id === ORINA_COMPLETA_ESTUDIO_ID || e.codigo === '134' || e.codigo === '1011' || e.codigo === '1015' || e.codigo === '416' || e.codigo === '5802' || e.codigo === '471' || e.codigo === '1130.24' || e.codigo === '1130.00' || e.codigo === '766' || e.codigo === '765' || e.codigo === '846' || e.codigo === '923' || e.codigo === '433' || e.codigo === '736' || e.codigo === '3734' || e.codigo === '169' || e.codigo === '110' || e.codigo === '6714' || e.codigo === '1105';
                const laboratorioSelect = (
                  <select
                    className={styles.labSelect}
                    value={item.laboratorioId}
                    onChange={(ev) => updateItem(e.id, { laboratorioId: Number(ev.target.value) })}
                  >
                    {laboratorios.map((l) => (
                      <option key={l.id} value={l.id}>{l.nombre}</option>
                    ))}
                  </select>
                );
                return (
                  <div key={e.id} className={styles.hemogramaBlock}>
                    <div className={styles.hemogramaHeader}>
                      <div className={`${styles.resultEstudio} ${styles.hemogramaHeaderTitle}`}>{e.descripcion}</div>
                      <div className={styles.resultRemoveCol}>
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => toggleEstudio(e)}
                          title="Quitar estudio"
                        >
                          ✕ Quitar
                        </button>
                      </div>
                    </div>
                    <div className={styles.hemogramaGrid}>
                      {getCampos(e.id, e.codigo).map((campo, i) => (
                        <div key={campo} className={styles.hemogramaField}>
                          <label className={styles.label}>{campo}</label>
                          <input
                            className={styles.resultInput}
                            type="text"
                            value={valores[i]}
                            onChange={(ev) => updateItem(e.id, { resultado: setSubValor(e.id, e.codigo, item.resultado, i, ev.target.value) })}
                            placeholder="..."
                          />
                        </div>
                      ))}
                      {labInline && (
                        <div className={styles.hemogramaField}>
                          <label className={styles.label}>Laboratorio</label>
                          {laboratorioSelect}
                        </div>
                      )}
                    </div>
                    {!labInline && (
                      <div className={`${styles.resultLabCol} ${styles.hemogramaLabCol}`}>
                        <label className={styles.label}>Laboratorio</label>
                        {laboratorioSelect}
                      </div>
                    )}
                    <div className={styles.studyObsBlock}>
                      <div className={styles.studyObsCheckRow}>
                        <label className={styles.studyObsCheckLabel}>
                          <input
                            type="checkbox"
                            checked={studyObsEnabled.has(e.id)}
                            onChange={(ev) => setStudyObsEnabled((prev) => {
                              const next = new Set(prev);
                              if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                              return next;
                            })}
                          />
                          Agregar observación
                        </label>
                        <label className={styles.studyObsCheckLabel}>
                          <input
                            type="checkbox"
                            checked={studyDiuresisEnabled.has(e.id)}
                            onChange={(ev) => setStudyDiuresisEnabled((prev) => {
                              const next = new Set(prev);
                              if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                              return next;
                            })}
                          />
                          Diuresis
                        </label>
                      </div>
                      {studyObsEnabled.has(e.id) && (
                        <textarea
                          className={styles.studyObsTextarea}
                          value={studyObs.get(e.id) ?? ''}
                          onChange={(ev) => setStudyObs((prev) => { const next = new Map(prev); next.set(e.id, ev.target.value); return next; })}
                          placeholder="Observación del estudio..."
                          rows={2}
                        />
                      )}
                      {studyDiuresisEnabled.has(e.id) && (
                        <input
                          className={styles.resultInput}
                          type="text"
                          value={studyDiuresis.get(e.id) ?? ''}
                          onChange={(ev) => setStudyDiuresis((prev) => { const next = new Map(prev); next.set(e.id, ev.target.value); return next; })}
                          placeholder="ej: 1.350 ml/24 hs"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── 6. Observación ── */}
          {/* <div className={styles.card}>
            <h2 className={styles.cardTitle}>
              <span className={styles.cardStep}>{selectedEstudios.length > 0 ? '6' : '5'}</span>
              Observación
              <span className={styles.cardTitleOptional}>(opcional)</span>
            </h2>
            <textarea
              className={styles.textarea}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Ingresá cualquier observación general sobre el análisis..."
              rows={3}
            />
          </div> */}

          <div className={styles.formFooter}>
            <Button type="submit">
              Revisar antes de guardar →
            </Button>
            {editMode && (
              <button
                type="button"
                className={styles.cancelEditBtn}
                onClick={() => navigate('/buscar-analisis')}
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
        )}
      </main>
    </div>
  );
}
